/**
 * htmlToBlocks — one-time converter from the old TipTap notes HTML into a
 * DocEditor block tree (Chunk 9d migration).
 *
 * Mapping (draft quality — porting becomes proofreading, not retyping):
 *   p                     → text block (inline HTML kept: bold/italic/links/code)
 *   h1–h4                 → heading block (props.level; h5/h6 clamp to 4)
 *   ul[data-type=taskList]→ checkbox blocks (props.checked)
 *   ul                    → bullet blocks (nested levels: text with "• " markers)
 *   ol                    → number blocks (nesting via props.indent)
 *   div[data-callout]     → callout block with converted children
 *   blockquote            → callout block (no quote type in the block editor)
 *   hr                    → divider block
 *   pre                   → one code block (raw text, not per-line inline code)
 *   table                 → table block (first row = headers, plain-text cells)
 */

import { createBlock } from './blockUtils';
import { stripHtml } from './caretUtils';
import { matchLanguage } from '../types/CodeBlock';

const escapeHtml = (text) =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const escapeAttr = (text) => escapeHtml(text).replace(/"/g, '&quot;');

const isBlankTextBlock = (block) =>
  block.type === 'text' && stripHtml(block.content || '').trim() === '';

/** Phrasing tags that must stay inside a single text block with neighbouring text. */
const INLINE_TAGS = new Set([
  'b', 'strong', 'i', 'em', 'u', 's', 'strike', 'del', 'code', 'a',
  'span', 'font', 'mark', 'sub', 'sup', 'br',
]);

const isInlineNode = (node) => {
  if (node.nodeType === Node.TEXT_NODE) return true;
  if (node.nodeType !== Node.ELEMENT_NODE) return false;
  return INLINE_TAGS.has(node.tagName.toLowerCase());
};

/**
 * Chrome clipboard HTML often puts a sentence with bold as siblings under
 * <body>: text, <b>, text. Treating each as its own block produced a line
 * break before/after the bold run. Serialize them as one inline HTML string.
 */
const serializeInline = (node) => {
  if (node.nodeType === Node.TEXT_NODE) {
    return escapeHtml(node.textContent || '');
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return '';
  const tag = node.tagName.toLowerCase();
  if (tag === 'br') return '<br>';

  let inner = '';
  node.childNodes.forEach((child) => {
    inner += serializeInline(child);
  });

  if (tag === 'span' || tag === 'font' || tag === 'mark') return inner;
  if (tag === 'a') {
    const href = (node.getAttribute('href') || '').trim();
    if (href && inner && /^(https?:\/\/|mailto:)/i.test(href)) {
      return `<a href="${escapeAttr(href)}">${inner}</a>`;
    }
    return inner;
  }
  const outTag =
    tag === 'strong' ? 'b'
      : tag === 'em' ? 'i'
        : tag === 'strike' || tag === 'del' ? 's'
          : tag;
  return inner ? `<${outTag}>${inner}</${outTag}>` : '';
};

/** Div with no callout marker and only phrasing content (Chrome fragment wrappers). */
const isTransparentDiv = (el) => {
  if (!el || el.nodeType !== Node.ELEMENT_NODE) return false;
  if (el.tagName.toLowerCase() !== 'div' || el.hasAttribute('data-callout')) return false;
  return Array.from(el.childNodes).every((n) => {
    if (n.nodeType === Node.COMMENT_NODE) return true;
    if (isInlineNode(n)) return true;
    return isTransparentDiv(n);
  });
};

/**
 * Chrome sometimes splits one sentence across divs around a bold/italic run:
 *   <div>before </div><div><b>bold</b></div><div> after</div>
 * A div whose only meaningful child is a formatting tag marks that pattern.
 */
const FORMATTING_TAGS = new Set(['b', 'strong', 'i', 'em', 'code', 'a', 'u', 's', 'strike', 'del']);
const isFormattingOnlyDiv = (el) => {
  if (!isTransparentDiv(el)) return false;
  const kids = Array.from(el.childNodes).filter((n) => {
    if (n.nodeType === Node.COMMENT_NODE) return false;
    if (n.nodeType === Node.TEXT_NODE) return n.textContent.trim().length > 0;
    return n.nodeType === Node.ELEMENT_NODE;
  });
  if (kids.length !== 1) return false;
  const only = kids[0];
  return (
    only.nodeType === Node.ELEMENT_NODE &&
    FORMATTING_TAGS.has(only.tagName.toLowerCase())
  );
};

export function htmlToBlocks(html) {
  if (!html || !stripHtml(html).trim()) return [];
  const dom = new DOMParser().parseFromString(html, 'text/html');
  const blocks = convertChildren(dom.body, 0);
  // Trim trailing blank paragraphs (TipTap often ends documents with them)
  while (blocks.length > 0 && isBlankTextBlock(blocks[blocks.length - 1])) {
    blocks.pop();
  }
  return blocks;
}

function convertChildren(parent, depth) {
  const blocks = [];
  let inlineBuf = '';

  const flushInline = () => {
    if (stripHtml(inlineBuf).trim()) {
      blocks.push(createBlock('text', inlineBuf.trim()));
    }
    inlineBuf = '';
  };

  const appendInlineTree = (node) => {
    if (node.nodeType === Node.COMMENT_NODE) return;
    if (isInlineNode(node)) {
      inlineBuf += serializeInline(node);
      return;
    }
    if (isTransparentDiv(node)) {
      node.childNodes.forEach(appendInlineTree);
    }
  };

  const childNodes = Array.from(parent.childNodes);
  for (let i = 0; i < childNodes.length; i += 1) {
    const node = childNodes[i];
    if (node.nodeType === Node.COMMENT_NODE) continue;

    // Flat inline siblings (text / <b> / text) → one text block
    if (isInlineNode(node)) {
      appendInlineTree(node);
      continue;
    }

    // Run of transparent divs that includes a formatting-only split → merge
    if (isTransparentDiv(node)) {
      let end = i;
      let sawFormattingSplit = isFormattingOnlyDiv(node);
      while (end + 1 < childNodes.length) {
        const next = childNodes[end + 1];
        if (next.nodeType === Node.COMMENT_NODE) {
          end += 1;
          continue;
        }
        if (!isTransparentDiv(next)) break;
        end += 1;
        if (isFormattingOnlyDiv(next)) sawFormattingSplit = true;
      }
      if (sawFormattingSplit) {
        for (let k = i; k <= end; k += 1) appendInlineTree(childNodes[k]);
        i = end;
        continue;
      }
      // Normal sibling divs (e.g. one checkbox line each) stay separate blocks
      flushInline();
      blocks.push(...convertNode(node, depth));
      continue;
    }

    flushInline();
    blocks.push(...convertNode(node, depth));
  }
  flushInline();
  return blocks;
}

function convertNode(node, depth) {
  // Text / inline nodes are coalesced by convertChildren — only blocks land here.
  if (node.nodeType !== Node.ELEMENT_NODE) return [];

  const el = node;
  const tag = el.tagName.toLowerCase();

  switch (tag) {
    case 'p':
      return [createBlock('text', el.innerHTML.trim())];

    case 'h1':
    case 'h2':
    case 'h3':
    case 'h4':
    case 'h5':
    case 'h6':
      return [createBlock('heading', el.innerHTML.trim(), { level: Math.min(Number(tag[1]), 4) })];

    case 'ul':
      if (el.getAttribute('data-type') === 'taskList') {
        return convertTaskList(el, depth);
      }
      return convertList(el, depth, false);

    case 'ol':
      return convertList(el, depth, true);

    case 'blockquote': {
      const quote = makeCallout(convertChildren(el, 0), '❝');
      quote.props.variant = 'quote';
      return [quote];
    }

    // Notion callouts: clipboard/export HTML uses <aside> (or
    // <figure class="callout">). Emoji icons travel as text/img alt and are
    // ported; Notion's built-in SVG icons (/icons/…) can't be, so those get
    // our default. The leading icon node is consumed so it doesn't become a
    // stray paragraph.
    case 'aside': {
      const { icon, host } = extractNotionCalloutIcon(el);
      // Portable emoji alt only — Notion SVG icons (/icons/…) leave the callout iconless
      return [makeCallout(convertChildren(host, 0), icon || null)];
    }

    case 'figure':
      if (el.classList.contains('callout')) {
        const { icon, host } = extractNotionCalloutIcon(el);
        return [makeCallout(convertChildren(host, 0), icon || null)];
      }
      return convertChildren(el, depth);

    case 'div':
      if (el.hasAttribute('data-callout')) {
        return [makeCallout(convertChildren(el, 0), null)];
      }
      // Generic wrapper — unwrap and keep going
      return convertChildren(el, depth);

    case 'hr':
      return [createBlock('divider')];

    case 'pre': {
      // Chat/GitHub/Cursor put fenced snippets in <pre><code> — keep as ONE
      // code block. (Previously each line became a text block wrapped in
      // <code>, which looked like "a code line per row".)
      const codeEl = el.querySelector('code');
      const langClass =
        (codeEl || el).getAttribute('class') ||
        el.getAttribute('data-language') ||
        '';
      const langHint =
        langClass.match(/language-([\w+-]+)/i)?.[1] ||
        el.getAttribute('data-language') ||
        '';
      // Preserve interior newlines/indent; only strip a single trailing \n
      // that browsers often append after the last line.
      const content = (el.textContent || '').replace(/\n$/, '');
      return [
        createBlock('code', content, {
          language: matchLanguage(langHint) || 'plaintext',
          wrapText: true,
          showLineNumbers: false,
        }),
      ];
    }

    case 'table':
      return convertTable(el);

    default: {
      // Unknown element: recurse into block children, else keep its text
      if (el.children.length > 0) return convertChildren(el, depth);
      const text = el.textContent.trim();
      return text ? [createBlock('text', escapeHtml(text))] : [];
    }
  }
}

/**
 * Pull the icon out of a Notion callout element. Returns { icon, host }:
 * icon is an emoji string or null, host is a clone with the icon node
 * removed so the content conversion doesn't render it as text.
 */
function extractNotionCalloutIcon(el) {
  const host = el.cloneNode(true);
  let icon = null;

  // <img src="/icons/…"> = Notion built-in icon (not portable). An emoji
  // icon travels as <span class="icon">🔥</span> or as the img's alt text.
  const img = host.querySelector(':scope > img, :scope > div > img, .icon img');
  if (img) {
    const alt = (img.getAttribute('alt') || '').trim();
    if (alt && !alt.startsWith('/') && !alt.includes('.svg') && alt.length <= 4) icon = alt;
    img.remove();
  }
  const iconSpan = host.querySelector('.icon');
  if (iconSpan) {
    const text = iconSpan.textContent.trim();
    if (text && text.length <= 4) icon = text;
    iconSpan.remove();
  }
  return { icon, host };
}

function makeCallout(children, icon) {
  const callout = createBlock('callout', '', icon ? { icon } : {});
  callout.children = children.length ? children : [createBlock('text')];
  return callout;
}

function convertTaskList(listEl, depth) {
  const blocks = [];
  const indentProps = depth ? { indent: Math.min(depth, 6) } : {};
  listEl.querySelectorAll(':scope > li').forEach((li) => {
    const checked = li.getAttribute('data-checked') === 'true';
    // TipTap taskItem: <li><label>…checkbox…</label><div><p>content</p>…</div></li>
    const host = li.querySelector(':scope > div') || li;
    const paras = Array.from(host.querySelectorAll(':scope > p'));

    let content;
    if (paras.length > 0) {
      content = paras[0].innerHTML.trim();
    } else {
      const clone = host.cloneNode(true);
      clone.querySelectorAll('label, input, ul, ol').forEach((n) => n.remove());
      content = clone.innerHTML.trim();
    }
    blocks.push(createBlock('checkbox', content, { checked, ...indentProps }));

    // Extra paragraphs inside the same item become plain text below it
    paras.slice(1).forEach((p) => blocks.push(createBlock('text', p.innerHTML.trim(), indentProps)));
    // Nested lists inside the item
    host.querySelectorAll(':scope > ul, :scope > ol').forEach((nested) => {
      blocks.push(...convertNode(nested, depth + 1));
    });
  });
  return blocks;
}

function convertList(listEl, depth, ordered) {
  const blocks = [];
  const indentProps = depth ? { indent: Math.min(depth, 6) } : {};
  const olStart = ordered ? parseInt(listEl.getAttribute('start'), 10) : NaN;
  listEl.querySelectorAll(':scope > li').forEach((li, i) => {
    const nestedLists = [];
    const inlineParts = [];
    li.childNodes.forEach((child) => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const childTag = child.tagName.toLowerCase();
        if (childTag === 'ul' || childTag === 'ol') {
          nestedLists.push(child);
          return;
        }
        if (childTag === 'p') {
          inlineParts.push(child.innerHTML.trim());
          return;
        }
        inlineParts.push(child.outerHTML);
        return;
      }
      const text = child.textContent.trim();
      if (text) inlineParts.push(escapeHtml(text));
    });

    const content = inlineParts.join(' ').trim();
    // Nesting carried as props.indent (Tab/Shift+Tab compatible)
    const liValue = ordered ? parseInt(li.getAttribute('value'), 10) : NaN;
    const start =
      ordered && i === 0 && Number.isFinite(olStart) && olStart > 1
        ? olStart
        : Number.isFinite(liValue) && liValue > 1
          ? liValue
          : null;
    const props = start != null ? { ...indentProps, start } : indentProps;
    blocks.push(createBlock(ordered ? 'number' : 'bullet', content, props));
    nestedLists.forEach((nested) => blocks.push(...convertNode(nested, depth + 1)));
  });
  return blocks;
}

function convertTable(tableEl) {
  // Real table block — cells keep basic inline HTML (bold/italic/code); the
  // paste sanitizer trims anything unsafe afterward.
  const matrix = [];
  tableEl.querySelectorAll('tr').forEach((tr) => {
    const cells = Array.from(tr.querySelectorAll(':scope > th, :scope > td')).map(
      (cell) => (cell.innerHTML || '').replace(/\s+/g, ' ').trim()
    );
    if (cells.length) matrix.push(cells);
  });
  if (matrix.length === 0) return [];

  // First row is the header (Notion/TipTap tables mark it with <th>, but a
  // plain first <tr> serves the same role); pad short rows to full width
  const width = Math.max(...matrix.map((r) => r.length));
  const pad = (row) => [...row, ...new Array(Math.max(0, width - row.length)).fill('')];
  const headers = pad(matrix[0]);
  const rows = matrix.slice(1).map(pad);

  return [
    createBlock('table', '', {
      tableData: { headers, rows: rows.length > 0 ? rows : [new Array(width).fill('')] },
    }),
  ];
}
